'use client';

import { useMemo } from 'react';

import { InfiniteTable, Meter } from '@web/ui';
import { formatHours } from '@web/utils';

import {
  clampPercentage,
  formatDueStatus,
  formatHoursWithSuffix,
  formatRemainingHoursWithSuffix,
} from './formatters';
import styles from './JobDetailsPerformanceView.module.scss';

import type {
  JobPerformanceDeliverableRow,
  JobPerformanceMetrics,
} from '@/features/jobs/types';

type DeliverableDriftSectionProps = {
  rows: JobPerformanceDeliverableRow[];
  overview: JobPerformanceMetrics;
};

type DeliverableDriftTableRow =
  | {
      id: string;
      kind: 'deliverable';
      row: JobPerformanceDeliverableRow;
    }
  | {
      id: 'totals';
      kind: 'totals';
      row: JobPerformanceMetrics;
    };

export function DeliverableDriftSection({
  rows,
  overview,
}: DeliverableDriftSectionProps) {
  const tableRows = useMemo<DeliverableDriftTableRow[]>(
    () => [
      ...rows.map((row) => ({
        id: row.deliverable.id,
        kind: 'deliverable' as const,
        row,
      })),
      {
        id: 'totals' as const,
        kind: 'totals' as const,
        row: overview,
      },
    ],
    [overview, rows],
  );

  const columns = useMemo(
    () => [
      {
        id: 'deliverable',
        header: 'DELIVERABLE NAME',
        accessor: (item: DeliverableDriftTableRow) =>
          item.kind === 'totals' ? 'Totals' : item.row.deliverable.name,
        sortable: true,
        minWidth: 210,
        cell: (item: DeliverableDriftTableRow) =>
          item.kind === 'totals' ? (
            <div className={styles.entityCell}>
              <span className={styles.totalsLabel}>Totals</span>
              <span className={styles.totalsMeta}>
                Remaining{' '}
                {formatRemainingHoursWithSuffix(overview.remaining_hours)}
              </span>
            </div>
          ) : (
            <div className={styles.entityCell}>
              <span className={styles.entityName}>
                {item.row.deliverable.name}
              </span>
              <span className={styles.entityMeta}>
                {item.row.deliverable.execution_state.replaceAll('_', ' ')}
              </span>
            </div>
          ),
      },
      {
        id: 'original_plan',
        header: 'ORIGINAL PLAN',
        accessor: (item: DeliverableDriftTableRow) =>
          item.row.original_plan_hours,
        sortable: true,
        minWidth: 90,
        cell: (item: DeliverableDriftTableRow) =>
          formatHoursWithSuffix(item.row.original_plan_hours),
      },
      {
        id: 'current_plan',
        header: 'CURRENT PLAN',
        accessor: (item: DeliverableDriftTableRow) =>
          item.row.current_plan_hours,
        sortable: true,
        minWidth: 90,
        cell: (item: DeliverableDriftTableRow) =>
          formatHoursWithSuffix(item.row.current_plan_hours),
      },
      {
        id: 'actual',
        header: 'ACTUAL',
        accessor: (item: DeliverableDriftTableRow) => item.row.actual_hours,
        sortable: true,
        minWidth: 80,
        cell: (item: DeliverableDriftTableRow) =>
          formatHoursWithSuffix(item.row.actual_hours),
      },
      {
        id: 'loe_meter',
        header: 'LOE METER',
        accessor: (item: DeliverableDriftTableRow) => item.row.actual_pct,
        sortable: true,
        minWidth: 180,
        cell: (item: DeliverableDriftTableRow) => {
          const dueStatus =
            item.kind === 'deliverable'
              ? formatDueStatus(
                  item.row.deliverable.due_date,
                  item.row.deliverable.days_to_due,
                )
              : '';
          const isOverdue =
            item.kind === 'deliverable' &&
            (item.row.deliverable.days_to_due ?? 0) < 0;

          return (
            <div className={styles.meterCell}>
              <Meter
                percentage={clampPercentage(item.row.actual_pct)}
                label={`${formatHours(item.row.actual_hours)} / ${formatHours(item.row.current_plan_hours)}h`}
                ariaLabel="Deliverable LOE meter"
              />
              {dueStatus ? (
                <span
                  className={
                    isOverdue ? styles.dueStatusOverdue : styles.entityMeta
                  }
                >
                  {dueStatus}
                </span>
              ) : null}
            </div>
          );
        },
      },
    ],
    [overview.remaining_hours],
  );

  return (
    <section className={styles.performanceSection}>
      <header className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Deliverable Drift</h3>
        <p className={styles.sectionSubtitle}>
          Largest overages first so scope movement and execution drift surface
          immediately.
        </p>
      </header>

      <InfiniteTable<DeliverableDriftTableRow>
        tableId="job-performance-deliverable-drift"
        columns={columns}
        rows={tableRows}
        emptyMessage="No deliverables found for this scope."
        hasMore={false}
        isLoading={false}
        getRowId={(row) => row.id}
        getRowSortPosition={(row) =>
          row.kind === 'totals' ? 'last' : 'normal'
        }
        rowHeight={74}
        virtualizedHeight="auto"
        onLoadMore={() => {}}
      />
    </section>
  );
}
